import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Users,
  CheckCircle,
  XCircle,
  Clock,
  MoreVertical,
  Shield,
  User,
  RefreshCw,
  FolderSearch
} from "lucide-react";

export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanLoading, setScanLoading] = useState(false);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error("Erreur lors du chargement des utilisateurs");
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleValidateUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/validate`);
      toast.success("Utilisateur validé");
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la validation");
    }
  };

  const handleUpdateRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role });
      toast.success(`Rôle mis à jour: ${role}`);
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du rôle");
    }
  };

  const handleUpdateStatus = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status`, { status });
      toast.success(`Statut mis à jour: ${status}`);
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const handleScanMedia = async () => {
    setScanLoading(true);
    try {
      await axios.post(`${API}/admin/scan-media`);
      toast.success("Scan des médias lancé");
    } catch (error) {
      toast.error("Erreur lors du scan");
    } finally {
      setScanLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: "badge-admin text-white",
      moderator: "badge-moderator text-white",
      user: "badge-user text-gray-300"
    };
    return (
      <Badge className={`${styles[role] || styles.user} px-2 py-1`}>
        {role === "admin" && <Shield className="w-3 h-3 mr-1" />}
        {role}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { icon: CheckCircle, color: "bg-green-500/20 text-green-400 border-green-500/30" },
      pending: { icon: Clock, color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
      suspended: { icon: XCircle, color: "bg-red-500/20 text-red-400 border-red-500/30" }
    };
    const { icon: Icon, color } = config[status] || config.pending;
    return (
      <Badge className={color}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="admin-users">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-['Outfit'] text-3xl font-bold text-white flex items-center gap-3">
                <Users className="w-8 h-8 text-violet-500" />
                Gestion des utilisateurs
              </h1>
              <p className="text-gray-400 mt-1">
                {users.length} utilisateur{users.length !== 1 ? "s" : ""} enregistré{users.length !== 1 ? "s" : ""}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="border-white/10 hover:bg-white/5"
                onClick={fetchUsers}
                data-testid="refresh-users"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
              <Button
                className="bg-violet-600 hover:bg-violet-500"
                onClick={handleScanMedia}
                disabled={scanLoading}
                data-testid="scan-media"
              >
                <FolderSearch className={`w-4 h-4 mr-2 ${scanLoading ? "animate-spin" : ""}`} />
                Scanner les médias
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
            <div className="glass rounded-xl p-4">
              <p className="text-gray-400 text-sm">Total</p>
              <p className="text-2xl font-bold text-white">{users.length}</p>
            </div>
            <div className="glass rounded-xl p-4">
              <p className="text-gray-400 text-sm">Actifs</p>
              <p className="text-2xl font-bold text-green-400">
                {users.filter((u) => u.status === "active").length}
              </p>
            </div>
            <div className="glass rounded-xl p-4">
              <p className="text-gray-400 text-sm">En attente</p>
              <p className="text-2xl font-bold text-amber-400">
                {users.filter((u) => u.status === "pending").length}
              </p>
            </div>
            <div className="glass rounded-xl p-4">
              <p className="text-gray-400 text-sm">Admins</p>
              <p className="text-2xl font-bold text-violet-400">
                {users.filter((u) => u.role === "admin").length}
              </p>
            </div>
          </div>

          {/* Users Table */}
          <div className="glass rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-gray-400">Utilisateur</TableHead>
                    <TableHead className="text-gray-400">Email</TableHead>
                    <TableHead className="text-gray-400">Rôle</TableHead>
                    <TableHead className="text-gray-400">Statut</TableHead>
                    <TableHead className="text-gray-400">Inscription</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow
                      key={user.user_id}
                      className="border-white/10 table-row"
                      data-testid={`user-row-${user.user_id}`}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img
                              src={user.picture}
                              alt={user.name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
                              <User className="w-4 h-4 text-violet-400" />
                            </div>
                          )}
                          <span className="text-white">{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-400">{user.email}</TableCell>
                      <TableCell>{getRoleBadge(user.role)}</TableCell>
                      <TableCell>{getStatusBadge(user.status)}</TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {user.created_at
                          ? new Date(user.created_at).toLocaleDateString("fr-FR")
                          : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              data-testid={`user-actions-${user.user_id}`}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="bg-[#171717] border-white/10"
                          >
                            <DropdownMenuLabel className="text-gray-400">
                              Actions
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-white/10" />
                            
                            {user.status === "pending" && (
                              <DropdownMenuItem
                                onClick={() => handleValidateUser(user.user_id)}
                                className="text-green-400 hover:text-green-300 hover:bg-green-500/10 cursor-pointer"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Valider
                              </DropdownMenuItem>
                            )}
                            
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuLabel className="text-gray-500 text-xs">
                              Changer le rôle
                            </DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() => handleUpdateRole(user.user_id, "user")}
                              className="cursor-pointer hover:bg-white/5"
                            >
                              Utilisateur
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleUpdateRole(user.user_id, "moderator")}
                              className="cursor-pointer hover:bg-white/5"
                            >
                              Modérateur
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleUpdateRole(user.user_id, "admin")}
                              className="cursor-pointer hover:bg-white/5"
                            >
                              Admin
                            </DropdownMenuItem>
                            
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuLabel className="text-gray-500 text-xs">
                              Changer le statut
                            </DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() => handleUpdateStatus(user.user_id, "active")}
                              className="text-green-400 cursor-pointer hover:bg-green-500/10"
                            >
                              Activer
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleUpdateStatus(user.user_id, "suspended")}
                              className="text-red-400 cursor-pointer hover:bg-red-500/10"
                            >
                              Suspendre
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
